import { captureException } from "@/exception-handler";

const STARTUP_RECOVERY_ATTEMPT_KEY = "bow.startup-recovery.attempted";
const STARTUP_RECOVERY_ROOT_ID = "bow-startup-recovery";
const STARTUP_RECOVERY_RELOAD_ID = "bow-startup-recovery-reload";
const STARTUP_RECOVERY_ERROR_ID = "bow-startup-recovery-error";

let recoveryPromise: Promise<void> | null = null;
let promptReady = false;
let failureHandled = false;

type StartupRecoveryShell = {
	appRoot: HTMLElement | null;
	recoveryRoot: HTMLElement;
	reloadButton: HTMLButtonElement;
	errorText: HTMLElement;
};

function isBrowser(): boolean {
	return typeof window !== "undefined" && typeof document !== "undefined";
}

function recoveryAttempted(): boolean {
	if (!isBrowser()) {
		return false;
	}

	try {
		return window.sessionStorage.getItem(STARTUP_RECOVERY_ATTEMPT_KEY) === "1";
	} catch {
		return false;
	}
}

function setRecoveryAttempted(attempted: boolean) {
	if (!isBrowser()) {
		return;
	}

	try {
		if (attempted) {
			window.sessionStorage.setItem(STARTUP_RECOVERY_ATTEMPT_KEY, "1");
			return;
		}

		window.sessionStorage.removeItem(STARTUP_RECOVERY_ATTEMPT_KEY);
	} catch {
		// Ignore storage failures; recovery still needs to proceed.
	}
}

function describeError(error: unknown): string {
	if (error instanceof Error) {
		return error.message;
	}

	if (typeof error === "string") {
		return error;
	}

	if (typeof error === "object" && error) {
		try {
			return JSON.stringify(error);
		} catch {
			return String(error);
		}
	}

	return String(error);
}

async function clearServiceWorkerState() {
	const tasks: Promise<unknown>[] = [];

	if (
		typeof navigator !== "undefined" &&
		"serviceWorker" in navigator &&
		typeof navigator.serviceWorker.getRegistrations === "function"
	) {
		tasks.push(
			navigator.serviceWorker
				.getRegistrations()
				.then((registrations) =>
					Promise.allSettled(
						registrations.map((registration) => registration.unregister()),
					),
				),
		);
	}

	if (typeof caches !== "undefined") {
		tasks.push(
			caches
				.keys()
				.then((keys) =>
					Promise.allSettled(keys.map((cacheName) => caches.delete(cacheName))),
				),
		);
	}

	await Promise.allSettled(tasks);
}

function getRecoveryShell(): StartupRecoveryShell | null {
	if (!isBrowser()) {
		return null;
	}

	const recoveryRoot = document.getElementById(STARTUP_RECOVERY_ROOT_ID);
	const reloadButton = document.getElementById(STARTUP_RECOVERY_RELOAD_ID);
	const errorText = document.getElementById(STARTUP_RECOVERY_ERROR_ID);

	if (
		!(recoveryRoot instanceof HTMLElement) ||
		!(reloadButton instanceof HTMLButtonElement) ||
		!(errorText instanceof HTMLElement)
	) {
		console.error(
			"[bow-startup-recovery] recovery shell missing from document",
		);
		return null;
	}

	return {
		appRoot: document.getElementById("app"),
		recoveryRoot,
		reloadButton,
		errorText,
	};
}

function resetRecoveryScreen() {
	const shell = getRecoveryShell();
	if (!shell) {
		return;
	}

	shell.recoveryRoot.hidden = true;
	shell.errorText.textContent = "";
	shell.reloadButton.onclick = null;

	if (shell.appRoot) {
		shell.appRoot.hidden = false;
	}
}

function renderRecoveryScreen(errorMessage: string) {
	const shell = getRecoveryShell();
	if (!shell) {
		return;
	}

	shell.recoveryRoot.hidden = false;
	shell.errorText.textContent = `Startup error: ${errorMessage}`;
	shell.reloadButton.onclick = () => {
		window.location.reload();
	};

	if (shell.appRoot) {
		shell.appRoot.hidden = true;
	}
}

async function handleStartupFailure(error: unknown) {
	if (!isBrowser() || promptReady || failureHandled) {
		return;
	}

	if (recoveryPromise) {
		return recoveryPromise;
	}

	const errorMessage = describeError(error);
	const alreadyAttempted = recoveryAttempted();
	const reportedError =
		error instanceof Error ? error : new Error(errorMessage);

	console.error("[bow-startup-recovery] startup failure detected", {
		error: errorMessage,
	});
	captureException(reportedError);
	failureHandled = true;

	recoveryPromise = (async () => {
		if (!alreadyAttempted) {
			setRecoveryAttempted(true);
			console.info(
				"[bow-startup-recovery] unregistering service workers and clearing caches",
			);
			await clearServiceWorkerState();
		}

		console.info("[bow-startup-recovery] rendering recovery screen");
		renderRecoveryScreen(errorMessage);
	})().finally(() => {
		recoveryPromise = null;
	});

	return recoveryPromise;
}

export function markStartupRecoveryReady() {
	if (!isBrowser()) {
		return;
	}

	promptReady = true;
	failureHandled = false;
	setRecoveryAttempted(false);
	resetRecoveryScreen();
	console.info("[bow-startup-recovery] prompt system ready");
}

export function resetStartupRecoveryForTests() {
	if (!isBrowser()) {
		return;
	}

	promptReady = false;
	failureHandled = false;
	recoveryPromise = null;
	setRecoveryAttempted(false);
	resetRecoveryScreen();
	document.getElementById("app")?.replaceChildren();
}

export { handleStartupFailure, STARTUP_RECOVERY_ATTEMPT_KEY };
